import { FC } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import {
  LinkedInIcon,
  FacebookIcon,
  LinkIcon,
  XIcon,
  YouTubeIcon,
  InstagramIcon,
} from '@/assets';

export type AboutSectionProps = SliceComponentProps<Content.AboutSectionSlice>;

const socialIconMap: Record<
  string,
  FC<{ fill?: string; width?: number; height?: number }>
> = {
  website: LinkIcon,
  linkedin: LinkedInIcon,
  x: XIcon,
  facebook: FacebookIcon,
  youtube: YouTubeIcon,
  instagram: InstagramIcon,
  other: LinkIcon,
};

// ── About Devsu — Dark Centered variation ───────────────────────────────────

const AboutDevsuDarkCentered: FC<{
  slice: Content.AboutSectionSliceAboutDevsuDarkCentered;
}> = ({ slice }) => {
  const {
    headline,
    description,
    social_links,
    copyright_text,
    background_pattern,
    transparent_background,
  } = slice.primary;

  const copyright =
    copyright_text ||
    `© ${new Date().getFullYear()} Devsu All rights reserved.`;

  return (
    <section
      data-slice-type="about_section"
      data-slice-variation="aboutDevsuDarkCentered"
      className={`relative overflow-hidden text-white ${
        transparent_background ? 'bg-transparent' : 'bg-[#0F423A]'
      }`}
    >
      {isFilled.image(background_pattern) && (
        <PrismicNextImage
          field={background_pattern}
          fill
          className="pointer-events-none absolute inset-0 object-cover opacity-40"
          fallbackAlt=""
        />
      )}

      <div className="relative z-10 mx-auto flex max-w-[900px] flex-col items-center gap-6 px-6 py-20 text-center md:py-24">
        {isFilled.richText(headline) && (
          <PrismicRichText
            field={headline}
            components={{
              heading2: ({ children }) => (
                <h2
                  className="text-[28px] font-bold leading-[1.15] md:text-[40px]"
                  style={{ textWrap: 'balance' }}
                >
                  {children}
                </h2>
              ),
              strong: ({ children }) => (
                <strong className="font-bold text-[#03BF9E]">{children}</strong>
              ),
            }}
          />
        )}

        {isFilled.richText(description) && (
          <div
            className="max-w-[760px] text-[15px] leading-[1.6] text-white/80 md:text-[16px]"
            style={{ textWrap: 'pretty' }}
          >
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => <p className="m-0">{children}</p>,
                strong: ({ children }) => (
                  <strong className="font-semibold text-white">
                    {children}
                  </strong>
                ),
              }}
            />
          </div>
        )}

        {social_links && social_links.length > 0 && (
          <div className="mt-2 flex items-center gap-5">
            {social_links.map((item, index) => {
              const IconComponent =
                socialIconMap[item.icon ?? 'other'] ?? LinkIcon;
              return (
                <PrismicNextLink
                  key={index}
                  field={item.url}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-white/20"
                  aria-label={item.icon ?? 'social link'}
                >
                  <IconComponent fill="white" width={18} height={18} />
                </PrismicNextLink>
              );
            })}
          </div>
        )}

        <p className="mt-10 text-[13px] text-white/45 md:text-[14px]">
          {copyright}
        </p>
      </div>
    </section>
  );
};

// ── Main component (dispatches by variation) ────────────────────────────────

const AboutSection: FC<AboutSectionProps> = ({ slice }) => {
  if (slice.variation === 'aboutDevsuDarkCentered') {
    return <AboutDevsuDarkCentered slice={slice} />;
  }

  const { headline, description, images, cta_text, cta_link, social_links } =
    slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="w-full px-6 md:px-10"
    >
      {/* Main about card */}
      <div className="mx-auto max-w-[1380px] rounded-[24px] bg-[#F5F5F0] px-8 py-12 md:px-[100px] md:py-[80px]">
        <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">
          {/* Left column: text + CTA + social */}
          <div className="flex flex-1 flex-col justify-center">
            <div className="mb-6 [&>h2]:text-[28px] [&>h2]:font-medium [&>h2]:leading-[1.2] [&>h2]:text-[#041118] md:[&>h2]:text-[42px]">
              <PrismicRichText field={headline} />
            </div>

            <div className="mb-8 text-base leading-relaxed text-[#041118]/80 [&>p+p]:mt-4 md:text-lg">
              <PrismicRichText field={description} />
            </div>

            {(cta_text || cta_link?.text) && (
              <div className="mb-8">
                {/* Override the editor-supplied URL: every CTA on a
                    lead-magnet page should scroll to the LeadMagnetWithForm
                    slice (`#lead-magnet-form`). */}
                <a
                  href="#lead-magnet-form"
                  className="inline-block rounded-full bg-[#00F557] px-8 py-3 text-base font-medium text-[#041118] transition-opacity hover:opacity-90"
                >
                  {cta_text && <span>{cta_text} </span>}
                  {cta_link?.text && (
                    <span className="underline">{cta_link.text}</span>
                  )}
                </a>
              </div>
            )}

            {social_links && social_links.length > 0 && (
              <div className="flex items-center gap-3">
                {social_links.map((item, index) => {
                  const IconComponent =
                    socialIconMap[item.icon ?? 'other'] ?? LinkIcon;
                  return (
                    <PrismicNextLink
                      key={index}
                      field={item.url}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-[#002726] transition-opacity hover:opacity-80"
                    >
                      <IconComponent fill="white" width={20} height={20} />
                    </PrismicNextLink>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right column: images */}
          {images && images.length > 0 && (
            <div className="flex flex-1 flex-col gap-4">
              {images.map((item, index) => (
                <PrismicNextImage
                  key={index}
                  field={item.image}
                  className="w-full rounded-[8px] object-cover"
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Copyright bar */}
      <div className="mx-auto max-w-[1380px] border-b border-t border-[#aaa] px-8 py-6 text-center text-base text-[#aaa] md:px-[100px] md:text-lg">
        <p>© {new Date().getFullYear()} Devsu All rights reserved.</p>
      </div>
    </section>
  );
};

export default AboutSection;
